// src/modules/purityAnalyzer/pages/XrfVerification.jsx
// XRF workflow: pending-verification queue + calibration-learning queue
// (propose → admin approve/reject → new dataset version).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Tabs, Tag, Typography, Space, Button, message, Popconfirm,
  Checkbox, Input, Modal, Form,
} from 'antd';
import AdvancedTable from '../../../components/UI/AdvancedTable';
import {
  SafetyCertificateOutlined, ExperimentOutlined, BulbOutlined,
  CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchTests, fetchLearningQueue, proposeLearning, approveLearning, rejectLearning,
} from '../api';
import useBranchScope from '../hooks/useBranchScope';
import { usePermissions } from '../../../hooks/usePermissions';
import KaratBadge from '../components/KaratBadge';
import { purityColor } from '../constants/purityConstants';

const { Title, Text } = Typography;

export default function XrfVerification() {
  const navigate = useNavigate();
  const { isHO } = useBranchScope();
  const { can } = usePermissions();
  const canApprove = can('purityAnalyzer', 'approveCalibration');
  const canVerify = can('purityAnalyzer', 'verifyXrf');

  const [pending, setPending] = useState([]);
  const [verified, setVerified] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v, q] = await Promise.all([
        fetchTests({ xrfStatus: 'PENDING', status: 'COMPLETED', page: 1, limit: 50 }),
        fetchTests({ xrfStatus: 'VERIFIED', page: 1, limit: 50 }),
        canApprove || canVerify ? fetchLearningQueue() : Promise.resolve(null),
      ]);
      setPending(p?.data?.data?.items || []);
      setVerified(v?.data?.data?.items || []);
      setQueue(q?.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, [canApprove, canVerify]);

  useEffect(() => { load(); }, [load]);

  const onPropose = async (xrfId) => {
    try {
      await proposeLearning(xrfId);
      message.success('Proposed as calibration point — awaiting approval');
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Propose failed');
    }
  };

  const onApprove = async () => {
    try {
      const values = await approveForm.validateFields();
      const res = await approveLearning(approveTarget._id, values);
      const version = res?.data?.data?.version;
      message.success(
        `Approved — new calibration version ${version?.versionNumber}${values.activate ? ' (ACTIVATED)' : ''}`
      );
      setApproveTarget(null);
      approveForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Approval failed');
    }
  };

  const onReject = async (xrfId) => {
    try {
      await rejectLearning(xrfId, {});
      message.success('Proposal rejected');
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Reject failed');
    }
  };

  const baseCols = [
    {
      title: 'Test #', dataIndex: 'testNumber', width: 100,
      render: (v, r) => <a onClick={() => navigate(`/purity/tests/${r._id}`)}>{v}</a>,
    },
    {
      title: 'Predicted', dataIndex: 'predictedPurity', width: 100,
      render: (v) => <Text strong style={{ color: purityColor(v) }}>{v?.toFixed(2)}%</Text>,
    },
    { title: 'Karat', dataIndex: 'estimatedKarat', width: 80, render: (v) => <KaratBadge karat={v} /> },
    { title: 'Density', dataIndex: 'goldDensity', width: 100, render: (v) => `${v} g/cm³` },
    ...(isHO ? [{ title: 'Branch', dataIndex: ['branch', 'name'], width: 120 }] : []),
    { title: 'Operator', dataIndex: ['operator', 'name'], width: 120 },
    {
      title: 'Date', dataIndex: 'createdAt', width: 130,
      render: (v) => dayjs(v).format('DD MMM · HH:mm'),
    },
  ];

  const items = [
    {
      key: 'pending',
      label: <span><ExperimentOutlined /> Pending Verification ({pending.length})</span>,
      children: (
        <AdvancedTable
          rowKey="_id" size="small" loading={loading} dataSource={pending}
          scroll={{ x: 'max-content' }}
          columns={[
            ...baseCols,
            {
              title: '', width: 130, fixed: 'right', key: 'verify',
              render: (_, r) => (
                <Button size="small" type="primary" icon={<SafetyCertificateOutlined />}
                  onClick={() => navigate(`/purity/tests/${r._id}?xrf=1`)}>
                  Verify
                </Button>
              ),
            },
          ]}
        />
      ),
    },
    {
      key: 'verified',
      label: <span><SafetyCertificateOutlined /> Verified ({verified.length})</span>,
      children: (
        <AdvancedTable
          rowKey="_id" size="small" loading={loading} dataSource={verified}
          scroll={{ x: 'max-content' }}
          columns={[
            ...baseCols,
            {
              title: 'XRF', width: 90, key: 'xrfPurity',
              render: (_, r) => <Text strong>{r.xrfResult?.xrfPurity}%</Text>,
            },
            {
              title: 'Abs Error', dataIndex: 'absoluteError', width: 90,
              render: (v) => (v != null ? `${v}%` : '—'),
            },
            {
              title: 'Learning', width: 160, fixed: 'right', key: 'learningStatus',
              render: (_, r) => {
                const status = r.xrfResult?.learningStatus;
                if (status === 'APPROVED') return <Tag color="green">IN DATASET</Tag>;
                if (status === 'PROPOSED') return <Tag color="blue">PROPOSED</Tag>;
                if (status === 'REJECTED') return <Tag color="red">REJECTED</Tag>;
                return canVerify && r.xrfResult ? (
                  <Button size="small" icon={<BulbOutlined />} onClick={() => onPropose(r.xrfResult._id)}>
                    Propose Point
                  </Button>
                ) : '—';
              },
            },
          ]}
        />
      ),
    },
    ...(canApprove ? [{
      key: 'learning',
      label: <span><BulbOutlined /> Learning Queue ({queue.length})</span>,
      children: (
        <AdvancedTable
          rowKey="_id" size="small" loading={loading} dataSource={queue}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'XRF #', dataIndex: 'xrfNumber', width: 100 },
            {
              title: 'Test', width: 100, key: 'testLink',
              render: (_, r) => r.test
                ? <a onClick={() => navigate(`/purity/tests/${r.test._id}`)}>{r.test.testNumber}</a>
                : '—',
            },
            {
              title: 'Density', width: 100, key: 'testDensity',
              render: (_, r) => (r.test ? `${r.test.goldDensity} g/cm³` : '—'),
            },
            {
              title: 'Predicted', width: 100, key: 'testPredicted',
              render: (_, r) => (r.test ? `${r.test.predictedPurity}%` : '—'),
            },
            { title: 'XRF Purity', dataIndex: 'xrfPurity', width: 100, render: (v) => <Text strong>{v}%</Text> },
            { title: 'Proposed By', dataIndex: ['proposedBy', 'name'], width: 130 },
            {
              title: 'Proposed', dataIndex: 'proposedAt', width: 120,
              render: (v) => (v ? dayjs(v).format('DD MMM · HH:mm') : '—'),
            },
            {
              title: '', width: 190, fixed: 'right', key: 'actions',
              render: (_, r) => (
                <Space>
                  <Button size="small" type="primary" icon={<CheckOutlined />}
                    onClick={() => setApproveTarget(r)}>
                    Approve
                  </Button>
                  <Popconfirm title="Reject this proposal?" onConfirm={() => onReject(r._id)}>
                    <Button size="small" danger icon={<CloseOutlined />}>Reject</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <SafetyCertificateOutlined style={{ marginRight: 10 }} />XRF Verification & Learning
      </Title>
      <Card size="small">
        <Tabs items={items} />
      </Card>

      <Modal
        title={`Approve Calibration Point — ${approveTarget?.xrfNumber || ''}`}
        open={!!approveTarget}
        onCancel={() => setApproveTarget(null)}
        onOk={onApprove}
        okText="Approve & Create Version"
      >
        <Text type="secondary">
          Approving clones the current dataset into a new calibration version containing this
          point (density {approveTarget?.test?.goldDensity} g/cm³ → purity {approveTarget?.xrfPurity}%).
          Previous versions remain available for rollback.
        </Text>
        <Form form={approveForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="notes" label="Decision Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="activate" valuePropName="checked">
            <Checkbox>Activate the new version immediately</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
